import { useEffect, useState, type ReactNode } from 'react';
import useSWR from 'swr';
import { generatePath } from 'react-router-dom';
import { EngineService } from '@/services/engine';
import { VideoPlayer } from '@/components/VideoPlayer';
import { StreamingMode } from '@/types/streaming';

import {
    EventInfoPanel,
    EventViewSkeleton,
    EventViewErrorState,
    EventLinkedEntityPanel,
    EventActionBar,
} from './components';
import { useQueryResultsStore } from '@/stores';
import { AutoHideOnScroll, ConfidenceBadge } from '@/components';
import { SidePanel } from '@/components/SidePanel';
import { usePlayerStateReporter } from '@/hooks';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { getEventEntitiesCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { EventViewStatus, type Event, type EventEntities } from '@/types/event';
import { getEventPlaybackStartTime } from '@/utils/event-duration';
import { useEventReady } from './hooks/useEventReady';
import { getEntityKindForClass } from '@/utils/detection-classes';

interface EventViewProps {
    event: Event & { confidence?: number };
    title?: string;
    onEventLoaded?: (title: string) => void;
    actionBar?: ReactNode;
    refetchEvent?: () => void;
}

export function EventView({
    event,
    title,
    onEventLoaded,
    actionBar,
    refetchEvent,
}: EventViewProps) {
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const { events, lastQueryParams } = useQueryResultsStore();
    const { onTimeUpdate, onPlayingChange, onLoopChange, onSeeked } =
        usePlayerStateReporter({
            isLive: false,
            playingEventId: event.id,
        });

    const scoredEvent = events.find((e) => e.event.id === event.id);
    const confidence = event.confidence || scoredEvent?.confidence;
    const focuses = scoredEvent?.focuses ?? event.focuses ?? [];
    const titleFallback = scoredEvent ? lastQueryParams?.text : undefined;

    // Scene events have no tracked_object/entities rows; the engine
    // serves them by id from scene_vector (no detections/confidence).
    const isScene = event.type === 'SCENE';

    const readiness = useEventReady({ event, focuses, refetchEvent });
    const loadedSource =
        readiness.status === EventViewStatus.READY
            ? readiness.source
            : undefined;

    const { data: entitiesData } = useSWR<EventEntities>(
        !isScene ? getEventEntitiesCacheKey(event.id) : null,
        () => EngineService.getEventEntities(event.id, event.type),
    );

    useEffect(() => {
        if (loadedSource && onEventLoaded) {
            onEventLoaded(event.description || loadedSource.displayName);
        }
    }, [loadedSource, event.description, onEventLoaded]);

    if (readiness.status === EventViewStatus.LOADING) {
        return <EventViewSkeleton />;
    }

    if (readiness.status === EventViewStatus.ERROR) {
        return (
            <EventViewErrorState
                reason={readiness.reason}
                onRetry={readiness.retry}
            />
        );
    }

    const { source, eventRange, boundingBoxProps } = readiness;
    const displayTitle = title || event.description || titleFallback;
    const headerShowsDescription = !title;
    const showDescription =
        Boolean(event.description) && !headerShowsDescription;
    const startTime = getEventPlaybackStartTime(event, focuses);

    const entityKind = getEntityKindForClass(event.classId);
    const hasLinkedEntity = Boolean(
        entitiesData?.persons?.length || entitiesData?.vehicles?.length,
    );
    const showLinkedEntityPanel =
        !isScene && Boolean(entityKind) && hasLinkedEntity;
    const eventReturnUrl = generatePath(AppRoutes.EVENT, {
        eventId: event.id,
    });

    const mainContent = (
        <div className="flex flex-col gap-6 min-w-0">
            <VideoPlayer
                src={source.src || ''}
                source={source}
                initialMode={StreamingMode.VOD}
                fullScreenTitle={source.displayName}
                controls
                crop
                defaultLoop={true}
                boundingBoxProps={boundingBoxProps}
                eventRange={eventRange}
                startTime={startTime}
                onTimeUpdate={onTimeUpdate}
                onPlayingChange={onPlayingChange}
                onLoopChange={onLoopChange}
                onSeeked={onSeeked}
            />
            <div>
                {confidence !== undefined && (
                    <ConfidenceBadge className="mb-2" value={confidence} />
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <h4 className="text-h4 text-white-100 line-clamp-2 w-fit max-w-full">
                            {displayTitle}
                        </h4>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {displayTitle}
                    </TooltipContent>
                </Tooltip>
                {showDescription ? (
                    <p className="text-md text-grey-100 mt-1 whitespace-pre-wrap">
                        {event.description}
                    </p>
                ) : null}
            </div>

            <EventInfoPanel
                event={{
                    ...event,
                    confidence,
                }}
                source={source}
                alertRule={entitiesData?.alertRules?.[0]}
            />
        </div>
    );

    return (
        <div className="relative h-full min-h-0 overflow-hidden pt-14">
            {showLinkedEntityPanel && (
                <SidePanel side="left">
                    <EventLinkedEntityPanel
                        eventId={event.id}
                        classId={event.classId}
                        returnUrl={eventReturnUrl}
                    />
                </SidePanel>
            )}
            <AutoHideOnScroll container={scrollContainer}>
                {actionBar ?? (
                    <EventActionBar
                        event={event}
                        source={readiness.source}
                        query={titleFallback}
                        confidence={confidence}
                    />
                )}
            </AutoHideOnScroll>
            <div className="h-full flex flex-col">
                <div
                    ref={setScrollContainer}
                    className="flex-1 overflow-y-auto scrollbar-hide px-4"
                >
                    <div className="max-w-content mx-auto py-8">
                        {mainContent}
                    </div>
                </div>
            </div>
        </div>
    );
}
